import FormCompanyImage from 'components/form/FormCompanyImage';
import FormText from 'components/form/FormText';
import FormWysiwygEditor from 'components/form/FormWysiwygEditor';
import IndicatorScreen from 'components/indicator/IndicatorScreen';
import { AppHelper } from 'helpers/appHelper';
import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { UseFieldArrayReturn, useFormContext } from 'react-hook-form';
import { useRecoilState } from 'recoil';
import { AcctState } from 'stores/atoms/acctState';
import { QuizEditFormStateType } from 'types/page/quizEditFormStateType';
import WsQuizEditQuestionOptions from './WsQuizEditQuestionOptions';

/**
 *　クイズ作成編集 設問設定コンポーネント
 */
export default function WsQuizEditQuestions(props: {
  readOnly: boolean;
  fieldArray: UseFieldArrayReturn<QuizEditFormStateType, 'questions', 'id'>;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const [acctState] = useRecoilState(AcctState);

  const { noticeMessageSuccess } = useAppState();
  const { postQuizAiOptions } = useApiState();

  const [processing, setProcessing] = useState(false);

  const {
    control,
    register,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext<QuizEditFormStateType>();

  const { append, move, remove, fields, update } = props.fieldArray;

  // 登録可能最大件数
  const limit = 25;

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 設問の順番を入れ替えます。
   */
  const onMove = (questionIndex: number, upper: boolean) => {
    // 先頭の場合は上に移動不可
    if (upper && questionIndex === 0) {
      return;
    }
    // 最終行の場合は下に移動不可
    if (!upper && questionIndex === fields.length - 1) {
      return;
    }
    // 要素の入れ替え
    move(questionIndex, upper ? questionIndex - 1 : questionIndex + 1);
  };

  /**
   * 設問を削除します。
   */
  const onRemove = (questionIndex: number) => {
    // 1件以下の場合は削除不可
    if (fields.length <= 1) {
      return;
    }
    // 要素の削除
    remove(questionIndex);
  };

  /**
   * 設問を追加します。
   */
  const onAppend = () => {
    // 10件以下の場合は削除不可
    if (fields.length >= limit) {
      return;
    }
    // 要素の追加
    append({
      options: [{ correctFlag: true }, {}, {}, {}],
    });
  };

  /**
   * 解説の内容をレポートメール内の解説にコピーします。
   */
  const onCopyDescription = (questionIndex: number) => {
    const questions = getValues().questions;

    questions[questionIndex].reportMailDescriptionHtml = questions[questionIndex].descriptionHtml;

    update(questionIndex, questions[questionIndex]);
  };

  /**
   *　AIによるクイズ作成を行います。
   */
  const onCreateOptions = (questionIndex: number) => {
    AppHelper.submit(processing, setProcessing, async () => {
      try {
        const values = getValues();
        const questions = getValues().questions;
        let isValid = true;

        clearErrors();

        if (AppHelper.isEmpty(questions[questionIndex].title)) {
          setError(`questions.${questionIndex}.title` as any, {
            type: 'required',
          });
          isValid = false;
        }

        if (AppHelper.isEmpty(values.title)) {
          setError('title' as any, {
            type: 'required',
          });
          isValid = false;
        }

        if (AppHelper.isEmpty(values.description)) {
          setError('description' as any, {
            type: 'required',
          });
          isValid = false;
        }

        if (!isValid) {
          return;
        }

        const response = await postQuizAiOptions({
          companyId: acctState.company.id,
          title: values.title || '',
          description: values.description || '',
          questionTitle: questions[questionIndex].title,
        });

        // 先に配列を空にする
        questions[questionIndex].options = [...response.options];
        questions[questionIndex].descriptionHtml = response.descriptionHtml;
        questions[questionIndex].reportMailDescriptionHtml = response.descriptionHtml;

        // 要素の更新
        // TODO:選択肢の文字列の変更をうまく伝播させられないので、update関数ではなく、一度消して入れ直す荒技で一時回避
        remove(questionIndex);
        setValue('questions', questions);
        // update(questionIndex, questions[questionIndex]);

        noticeMessageSuccess('AIによる選択肢生成が完了しました。');
      } catch (e) {
        showBoundary(e);
      }
    });
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {/* ローディング */}
      <IndicatorScreen visible={processing} />

      <section className="section-form-default section-question">
        <div className="container">
          {getValues()?.questions.map((question, questionIndex) => {
            return (
              <div key={`ws-quiz-edit-questions-${questionIndex}`} className="box-common mb-3">
                <h3 className="title-section">設問{questionIndex + 1}</h3>

                <div className="form-default-container">
                  <div className="row row-0">
                    <div className="col-md col-header">
                      <div className="card form-header">
                        <h4 className="title">設問用の画像</h4>
                        <FormCompanyImage
                          control={control}
                          name={`questions.${questionIndex}.questionImageKey`}
                          readOnly={props.readOnly}
                        />
                      </div>
                    </div>
                    <div className="col-md col-form ps-md-5 ms-md-2">
                      <div className="btn-group-header text-end">
                        <div className="btn-group">
                          <button
                            className="btn"
                            onClick={() => onMove(questionIndex, true)}
                            disabled={props.readOnly}
                          >
                            <img src="/img/icon/arrow-up-black.svg" alt="" />
                          </button>
                          <button
                            className="btn"
                            onClick={() => onMove(questionIndex, false)}
                            disabled={props.readOnly}
                          >
                            <img src="/img/icon/arrow-up-black.svg" alt="" className="scaley-1" />
                          </button>
                          <button
                            className="btn"
                            onClick={() => onRemove(questionIndex)}
                            disabled={props.readOnly}
                          >
                            <img src="/img/icon/trash.svg" className="filter-black" />
                          </button>
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="privacy-policy" className="form-label">
                          設問
                        </label>
                        <div className="row align-items-center">
                          <div className="col-md">
                            <FormText
                              name={`questions.${questionIndex}.title`}
                              placeholder="設問を入れよう"
                              register={register}
                              errors={errors}
                              readOnly={props.readOnly}
                              maxLength={100}
                            />
                          </div>
                          <div className="col-md col-btn flex-grow-0">
                            <div className="btn-container max-width-130 mt-md-0">
                              <button
                                onClick={() => onCreateOptions(questionIndex)}
                                disabled={props.readOnly}
                                className="btn btn-common moderate-blue w-100"
                              >
                                選択肢の生成
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* オプション */}
                      <WsQuizEditQuestionOptions
                        questionIndex={questionIndex}
                        readOnly={props.readOnly}
                      />
                    </div>
                  </div>
                  <div className="row row-0">
                    <div className="col-md col-header">
                      <div className="card form-header">
                        <h4 className="title">回答用の画像</h4>

                        <FormCompanyImage
                          control={control}
                          name={`questions.${questionIndex}.answerImageKey`}
                          readOnly={props.readOnly}
                        />
                      </div>
                    </div>
                    <div className="col-md col-form ps-md-5 ms-md-2">
                      <div className="form-group">
                        <FormWysiwygEditor
                          control={control}
                          subject="解説"
                          name={`questions.${questionIndex}.descriptionHtml`}
                          placeholder="上記の設問の解説文を入れましょう。この部分はユーザーが回答を選んだ後に表示されます。"
                          errors={errors}
                          readOnly={props.readOnly}
                        />
                      </div>
                      <div className="form-group">
                        <div className="row">
                          <div className="col">
                            <label htmlFor="comment" className="form-label">
                              レポートメール内の解説
                            </label>
                          </div>
                          <div className="col text-end">
                            <a
                              onClick={() => onCopyDescription(questionIndex)}
                              className="text-note text-gray text-end mt-1 mb-0 hover"
                            >
                              解説をコピーして貼り付ける
                            </a>
                          </div>
                        </div>
                        <FormWysiwygEditor
                          control={control}
                          name={`questions.${questionIndex}.reportMailDescriptionHtml`}
                          placeholder="上記の設問の解説文を入れましょう。この部分はユーザーにメールで送られます。"
                          toolbar={['link']}
                          errors={errors}
                          readOnly={props.readOnly}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          <div className="btn-container max-width-238 mx-auto">
            <button
              onClick={onAppend}
              disabled={fields.length >= limit || props.readOnly}
              className="btn btn-common moderate-blue w-100"
            >
              + 設問を追加
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
